/**
 * eSanza Super Admin — Route metadata
 * Defines navigation order, labels, and route paths for the platform console.
 * Import order matches the brief: Overview, Organisations, Platform Users,
 * Subscriptions, Website Inbox, Audit Logs, Platform Settings.
 */

export interface AdminRoute {
  key: string;
  label: string;
  path: string;
  icon?: string; // Heroicons name
  group?: 'platform' | 'marketplace' | 'governance' | 'system';
  badge?: string;
}

export const ADMIN_ROUTES: AdminRoute[] = [
  {
    key: 'overview',
    label: 'Platform Overview',
    path: '/dashboard/super-admin',
    icon: 'HomeIcon',
    group: 'platform',
  },
  {
    key: 'organisations',
    label: 'Organisations',
    path: '/dashboard/company-management',
    icon: 'BuildingOffice2Icon',
    group: 'platform',
  },
  {
    key: 'users',
    label: 'Platform Users',
    path: '/dashboard/platform-users',
    icon: 'UsersIcon',
    group: 'platform',
  },
  {
    key: 'marketplace-overview',
    label: 'Marketplace Overview',
    path: '/marketplace/admin',
    icon: 'ShoppingCartIcon',
    group: 'marketplace',
  },
  {
    key: 'marketplace-suppliers',
    label: 'Suppliers',
    path: '/marketplace/admin/suppliers',
    icon: 'BuildingStorefrontIcon',
    group: 'marketplace',
  },
  {
    key: 'marketplace-verification',
    label: 'Supplier Verification',
    path: '/marketplace/admin/verification',
    icon: 'ShieldCheckIcon',
    group: 'marketplace',
  },
  {
    key: 'marketplace-orders',
    label: 'Orders & Fulfilment',
    path: '/marketplace/admin/orders',
    icon: 'ClipboardDocumentListIcon',
    group: 'marketplace',
  },
  {
    key: 'marketplace-finance',
    label: 'Finance & Settlements',
    path: '/marketplace/admin/finance',
    icon: 'CurrencyDollarIcon',
    group: 'marketplace',
  },
  {
    key: 'marketplace-settings',
    label: 'Marketplace Settings',
    path: '/marketplace/admin/settings',
    icon: 'Cog6ToothIcon',
    group: 'marketplace',
  },
  {
    key: 'subscriptions',
    label: 'Subscriptions',
    path: '/dashboard/subscriptions',
    icon: 'CreditCardIcon',
    group: 'governance',
  },
  {
    key: 'inbox',
    label: 'Website Inbox',
    path: '/dashboard/website-control',
    icon: 'EnvelopeIcon',
    group: 'governance',
  },
  {
    key: 'audit',
    label: 'Audit Logs',
    path: '/dashboard/audit-logs',
    icon: 'ClipboardDocumentListIcon',
    group: 'governance',
  },
  {
    key: 'settings',
    label: 'Platform Settings',
    path: '/dashboard/platform-settings',
    icon: 'Cog6ToothIcon',
    group: 'system',
  },
];

/**
 * Group routes by section for sidebar rendering.
 * Returns entries in order: platform, marketplace, governance, system.
 */
export function getGroupedRoutes(): Array<{ group: string; routes: AdminRoute[] }> {
  const groups = new Map<string, AdminRoute[]>();
  for (const route of ADMIN_ROUTES) {
    const g = route.group || 'platform';
    if (!groups.has(g)) groups.set(g, []);
    groups.get(g)!.push(route);
  }
  const order = ['platform', 'marketplace', 'governance', 'system'];
  const result: Array<{ group: string; routes: AdminRoute[] }> = [];
  for (const key of order) {
    const routes = groups.get(key);
    if (routes && routes.length > 0) {
      result.push({ group: key, routes });
    }
  }
  return result;
}

/** Section display labels */
export const GROUP_LABELS: Record<string, string> = {
  platform: 'Platform',
  marketplace: 'Marketplace',
  governance: 'Governance',
  system: 'System',
};
